import moment from "moment"
import "moment/locale/fr"

import {
    resolveSummerPricing,
    SUMMER_HOURS_PER_MONTH,
} from "../../summer/constants/summerContent"
import { formatActivationDate } from "./activationSuiteDisplay"

// Canonical helper — single entry point for every summer date used by the
// front. Reads from the backend `staticData.summer_config` payload when
// available, falls back to today's calendar year + the historical month-day
// values so the helpers still work pre-staticData / offline.
//
// Shape returned (all Y-m-d strings unless noted):
//   - year:          current operational year (number)
//   - cockpitOpen:   cockpit becomes accessible (default Y-05-15)
//   - cockpitClose:  cockpit window closes (default Y-08-31 — same as
//                    `windowEnd` historically; kept as a separate field so
//                    the cockpit "visible" gate and the pause window aren't
//                    coupled if they diverge in the future)
//   - windowStart:   pause window opens (default Y-07-01)
//   - windowEnd:     pause window closes (default Y-08-31)
//   - resumeDate:    auto-resume date = windowEnd + 1 (default Y-09-01)
//   - resumeEnd:     reprise window close (default Y-09-07)
//   - engagementCut: engagement-tolerance cutoff for renewal discount
//                    (default Y-07-31)
//   - graceDays:     post-window grace period for cockpit visibility (int)
//   - cockpitEnabled: interrupteur saisonnier de la surface parent (bool).
//                    Absent du payload => true (le comportement d'avant
//                    l'interrupteur) ; seul un `false` explicite éteint.
export const getSummerConfigSafe = (summerConfig) => {
    const year = summerConfig?.current_year ?? moment().year()
    const cockpitOpen = summerConfig?.cockpit_open ?? `${year}-05-15`
    const windowStart = summerConfig?.window_start ?? `${year}-07-01`
    const windowEnd = summerConfig?.window_end ?? `${year}-08-31`
    // Backend `cockpit_accessible_until` = windowEnd + grace_days. Front
    // historically used `windowEnd` itself for the visibility cap (no
    // grace), so we keep the historical default unless the backend ships
    // an explicit value.
    const cockpitClose = summerConfig?.cockpit_accessible_until ?? windowEnd
    const resumeDate =
        summerConfig?.resume_date ??
        moment(windowEnd).add(1, "day").format("YYYY-MM-DD")
    const resumeEnd =
        summerConfig?.resume_window_end ??
        moment(resumeDate).add(6, "days").format("YYYY-MM-DD")
    const engagementCut =
        summerConfig?.engagement_tolerance_end ?? `${year}-07-31`
    const graceDays =
        typeof summerConfig?.post_window_grace_days === "number"
            ? summerConfig.post_window_grace_days
            : 7
    // `staticData.summer_config.cockpit_enabled` (back : config
    // `summer.cockpit.parent_enabled`). Un payload qui ne porte pas le champ
    // — build ancien, contexte d'activation qui n'expose que les dates de
    // reprise — garde l'ancien comportement piloté par la fenêtre.
    const cockpitEnabled = summerConfig?.cockpit_enabled !== false
    return {
        year,
        cockpitOpen,
        cockpitClose,
        windowStart,
        windowEnd,
        resumeDate,
        resumeEnd,
        engagementCut,
        graceDays,
        cockpitEnabled,
    }
}

export const SUMMER_CHOICE_STATUS = {
    NOT_VISITED: "not_visited",
    VIEWED_NO_CHOICE: "viewed_no_choice",
    CHOSE_SUMMER_COURSES: "chose_summer_courses",
    CHOSE_AUTONOMY: "chose_autonomy",
    CHOSE_PAUSE: "chose_pause",
    AUTO_PAUSED: "auto_paused",
}

// Que se passe-t-il entre maintenant et le 1er juillet ?
// Le parent peut décider de ne rien changer, pauser tout de suite,
// passer en Autonomie immédiat, ou booster (examens) jusqu'au 30/06.
export const JUNE_TRANSITION = {
    NOT_SET: "not_set",
    CONTINUE_CURRENT: "continue_current",
    PAUSE_EARLY: "pause_early",
    AUTONOMY_NOW: "autonomy_now",
    BOOST_EXAMS: "boost_exams",
}

// Projection à la rentrée (1er septembre) — le parent indique sa formule
// de reprise. Edit possible jusqu'au 31/08. "Undecided" est valide.
// Les paliers reflètent le catalogue Stripe via
// Subscription::$pricesAmountsV2 :
//   - Cours partagés (essential) : 1h / 2h / 3h / 4h / 5h / sem
//   - Cours particuliers (integral) : 1h / 1.5h / 2h / 3h / 4h / 5h / sem
// `hours_6h` est conservé pour compat backward (legacy / QA) mais n'est
// plus exposé dans l'UI Cockpit. `hours_1_5h` est exclusif Particuliers.
export const RESUME_PREFERENCE = {
    NOT_SET: "not_set",
    HOURS_1H: "hours_1h",
    HOURS_1_5H: "hours_1_5h",
    HOURS_2H: "hours_2h",
    HOURS_3H: "hours_3h",
    HOURS_4H: "hours_4h",
    HOURS_5H: "hours_5h",
    HOURS_6H: "hours_6h",
    AUTONOMY: "autonomy",
    UNDECIDED: "undecided",
}

export const SUMMER_STAGE_WEEKS = {
    WEEK_1: {
        id: 1,
        label: "Semaine 1",
        dates: "17 → 21 août",
        start: "2026-08-17",
        end: "2026-08-21",
    },
    WEEK_2: {
        id: 2,
        label: "Semaine 2",
        dates: "24 → 28 août",
        start: "2026-08-24",
        end: "2026-08-28",
    },
}

// STAGE_PRICE = repli défensif du prix d'une semaine de stage, utilisé seulement
// quand aucun stage n'est chargé. Le prix qui s'affiche réellement vient du
// backend (`holidayCourse.price`, une ligne par stage posée par l'admin) — c'est
// lui la source de vérité, pas cette constante.
// Pour AUTONOMY_MONTHLY_PRICE et SUMMER_COURSES_PRICE, préférer les valeurs
// servies par `staticData.summer_config.pricing` là où elles sont disponibles.
export const SUMMER_PRICES = {
    SUMMER_COURSES_PRICE: 79,
    SUMMER_COURSES_OLD_PRICE: 169,
    AUTONOMY_MONTHLY_PRICE: 19,
    STAGE_PRICE: 119,
}

// Type de cours d'été. SHARED = cours collectifs (1 créneau hebdo fixe
// par enfant, pricing forfaitaire). PRIVATE = cours particuliers
// (1-5h/sem libre, pricing à l'heure).
export const SUMMER_COURSES_OFFER_TYPE = {
    SHARED: "shared",
    PRIVATE: "private",
}

// CATALOGUE des créneaux Cours partagés : l'enfant choisit parmi les créneaux de
// l'édition (lun→ven × horaires). Le prix suit le nombre de créneaux via la grille
// funnel (1 créneau = 1 h = 1 séance).
// ⚠️ Les `slot` sont stockés au format canonical `slots_possible`
// ("Day HH:MM-HH:MM"), même shape que `User.slots_possible` côté backend
// (parsable par `StudentTrait::parseAvailabilitySlot()`). Single source
// of truth : `config/summer.php` → `shared_timeslots`, exposé via
// `staticData.summer_config.shared_timeslots`. Garder cette liste synchro
// avec la config backend. Édition 5×3 : lun→ven × {matin 10h30-11h30,
// matin 11h30-12h30, soir 18h30-19h30}, 15 créneaux d'1 h. `dayLabel`/`timeLabel`
// sont les labels FR affichés côté FE — découplés du slot pour pouvoir afficher
// "10h30-11h30" tout en stockant "10:30-11:30".
//
// Catalogue ≠ offre en vente : un créneau SATURÉ ({@see SUMMER_FULL_TIMESLOTS})
// reste listé ici pour qu'une famille déjà placée garde un label résolvable et une
// re-soumission valide, mais n'est plus proposé à la sélection.
const SUMMER_SHARED_TIMES = [
    { time: "10:30-11:30", timeLabel: "10h30-11h30" },
    { time: "11:30-12:30", timeLabel: "11h30-12h30" },
    { time: "18:30-19:30", timeLabel: "18h30-19h30" },
]

const SUMMER_SHARED_DAYS = [
    { day: "Monday", dayLabel: "Lundi" },
    { day: "Tuesday", dayLabel: "Mardi" },
    { day: "Wednesday", dayLabel: "Mercredi" },
    { day: "Thursday", dayLabel: "Jeudi" },
    { day: "Friday", dayLabel: "Vendredi" },
]

export const SUMMER_SHARED_TIMESLOTS = SUMMER_SHARED_DAYS.flatMap(
    ({ day, dayLabel }) =>
        SUMMER_SHARED_TIMES.map(({ time, timeLabel }) => ({
            slot: `${day} ${time}`,
            dayLabel,
            timeLabel,
        }))
)

export const SUMMER_SHARED_TIMESLOT_SLOTS = SUMMER_SHARED_TIMESLOTS.map(
    (t) => t.slot
)

// Créneaux SATURÉS — repli hors-ligne du `full_timeslots` servi par
// `staticData.summer_config` (config backend `summer.full_timeslots`). Le matin
// 10h30 est saturé depuis l'édition 2026 (bien plus de demandes que de tuteurs
// staffables), d'où l'ouverture du 11h30-12h30.
export const SUMMER_FULL_TIMESLOTS = [
    "Monday 10:30-11:30",
    "Tuesday 10:30-11:30",
    "Wednesday 10:30-11:30",
    "Thursday 10:30-11:30",
    "Friday 10:30-11:30",
]

// Créneaux saturés, backend prioritaire, repli sur la constante hors-ligne. Un
// tableau vide servi par l'API est respecté (= plus aucun créneau saturé).
export const getSummerFullTimeslots = (summerConfig = null) =>
    Array.isArray(summerConfig?.full_timeslots)
        ? summerConfig.full_timeslots
        : SUMMER_FULL_TIMESLOTS

// Créneaux EN VENTE : le catalogue moins les saturés.
export const getSummerOfferedTimeslots = (summerConfig = null) => {
    const full = new Set(getSummerFullTimeslots(summerConfig))
    return SUMMER_SHARED_TIMESLOT_SLOTS.filter((slot) => !full.has(slot))
}

// Créneaux groupés par jour pour la grille de sélection (une ligne par jour,
// portant ses options). `period` ("morning"/"evening") est dérivé de l'heure de
// début → pilote l'icône ☀/🌙 du chip.
export const SUMMER_SHARED_TIMESLOTS_BY_DAY = SUMMER_SHARED_TIMESLOTS.reduce(
    (rows, t) => {
        const startHour = parseInt(t.slot.split(" ")[1], 10)
        const option = { ...t, period: startHour < 14 ? "morning" : "evening" }
        const row = rows.find((r) => r.dayLabel === t.dayLabel)
        if (row) row.options.push(option)
        else rows.push({ dayLabel: t.dayLabel, options: [option] })
        return rows
    },
    []
)

// Lignes de la grille réduites aux créneaux PROPOSABLES : les saturés disparaissent,
// SAUF ceux que la famille détient déjà (`keepSlots`) — elle doit continuer à voir
// son créneau et pouvoir le décocher, jamais le re-cocher une fois libéré. Un jour
// dont il ne reste aucune option n'est pas rendu.
export const getSummerSharedTimeslotsByDay = (
    summerConfig = null,
    keepSlots = []
) => {
    const full = new Set(getSummerFullTimeslots(summerConfig))
    const kept = new Set(keepSlots || [])
    return SUMMER_SHARED_TIMESLOTS_BY_DAY.map((row) => ({
        ...row,
        options: row.options.filter(
            (o) => !full.has(o.slot) || kept.has(o.slot)
        ),
    })).filter((row) => row.options.length > 0)
}

// Default cours partagés selection — the 3-morning hero pack (lun/mar/mer
// 11h30-12h30, le matin EN VENTE : un défaut sur un créneau saturé se ferait
// refuser par la vente différée). The picker exposes every créneau on sale for
// free selection, so the default is pinned here instead of "everything offered".
export const SUMMER_COURSES_DEFAULT_SLOTS = [
    "Monday 11:30-12:30",
    "Tuesday 11:30-12:30",
    "Wednesday 11:30-12:30",
]

// Le pack par défaut, garanti EN VENTE d'après la config servie. La constante
// ci-dessus est un miroir tenu à la main : si le backend ferme un créneau qu'elle
// croit ouvert, un défaut non filtré serait coché tout en étant masqué de la grille
// (le « déjà détenu » ne couvre que la sélection d'origine de la famille, pas le
// défaut) — un créneau invisible et impossible à décocher. On retombe sur les 3
// premiers créneaux en vente quand le pack phare n'est plus proposable.
export const getSummerCoursesDefaultSlots = (summerConfig = null) => {
    const offered = new Set(getSummerOfferedTimeslots(summerConfig))
    const kept = SUMMER_COURSES_DEFAULT_SLOTS.filter((slot) => offered.has(slot))
    if (kept.length) return kept
    return getSummerOfferedTimeslots(summerConfig).slice(
        0,
        SUMMER_COURSES_DEFAULT_SLOTS.length
    )
}

// Heures/semaine sélectionnables pour Cours particuliers uniquement.
export const SUMMER_COURSES_PRIVATE_HOURS_PER_WEEK = [1, 2, 3, 4, 5]

// Cours particuliers = offre ENGAGÉE : le €/h affiché est le tarif engagé
// `integral` du backend, servi PAR PALIER d'heures/mois via
// `staticData.summer_config.cours_particuliers_per_hour_by_month` (26 €/h à
// 1h/sem soit 4h/mois, 24 €/h au-delà) — display == charge. Le prix mensuel est
// `€/h(palier) × heures/mois` (cadence ~4,33 sem/mois, pas ×4), sans promo. Le
// tarif Cours PARTAGÉS vient lui de la grille fast-track backend
// (`resolveSummerPricing` + `staticData.summer_config.pricing_grid`). La
// constante ci-dessous n'est qu'un repli hors-ligne du taux engagé dominant.
export const SUMMER_COURSES_PRIVATE_PER_HOUR = 24

// Miroir hors-ligne de la grille engagée backend (`integral` commitment), par
// palier d'heures/mois. Utilisé UNIQUEMENT quand staticData est absente, pour
// garder le tarif 1h/sem (4h/mois) à 26 €/h plutôt que de retomber sur le
// forfait dominant 24 €/h. Garder synchro avec `pricesAmountsV2.integral`
// (backend) si la grille change.
export const SUMMER_COURSES_PRIVATE_PER_HOUR_BY_MONTH = {
    4: 26,
    6: 24,
    9: 24,
    13: 24,
    17: 24,
    22: 24,
}

// Taux horaire engagé Cours particuliers pour un palier d'heures/mois donné :
// grille backend par palier prioritaire (`cours_particuliers_per_hour_by_month`),
// puis taux forfaitaire backend (`cours_particuliers_per_hour`), puis — hors-ligne
// seulement — le miroir par palier ci-dessus, et enfin la constante forfaitaire.
// Le prix mensuel est `taux × heures/mois`, donc le €/h affiché vaut exactement
// le taux engagé servi.
export const getSummerCoursesPrivatePerHour = (
    summerConfig = null,
    hoursPerMonth = null
) => {
    const grid = summerConfig?.cours_particuliers_per_hour_by_month
    if (hoursPerMonth != null && grid) {
        const fromGrid = grid?.[String(hoursPerMonth)] ?? grid?.[hoursPerMonth]
        if (Number.isFinite(fromGrid) && fromGrid > 0) return fromGrid
    }
    const fromConfig = summerConfig?.cours_particuliers_per_hour
    if (Number.isFinite(fromConfig) && fromConfig > 0) return fromConfig
    // Hors-ligne (pas de staticData) : miroir par palier avant le forfait, pour
    // ne pas sous-évaluer le palier 1h/sem (4h/mois → 26 €/h).
    if (hoursPerMonth != null) {
        const fromMirror = SUMMER_COURSES_PRIVATE_PER_HOUR_BY_MONTH[hoursPerMonth]
        if (Number.isFinite(fromMirror) && fromMirror > 0) return fromMirror
    }
    return SUMMER_COURSES_PRIVATE_PER_HOUR
}

// Heures hebdo dérivées du config — `slots_possible.length` pour shared,
// `hoursPerWeek` pour private.
export const getSummerCoursesHoursPerWeek = (config) => {
    if (!config) return 0
    if (config.offerType === SUMMER_COURSES_OFFER_TYPE.SHARED) {
        return config.slots_possible?.length ?? 0
    }
    return config.hoursPerWeek ?? 0
}

// Heures/mois d'un pack de créneaux d'été (cours partagés OU particuliers) :
// grille backend (funnel `hours_per_month`, ~4,33 sem/mois → 4/9/13/17/22…)
// servie par `staticData.summer_config`, repli sur la grille constante miroir
// puis ×4,33 arrondi hors-ligne — jamais ×4. Le cockpit montre exactement les
// heures que la vente différée facture (`funnelHoursGrid`), online comme offline.
const WEEKS_PER_MONTH = 4.33

const coursesHoursPerMonth = (weeklyHours, summerConfig) => {
    const grid = summerConfig?.hours_per_month
    const fromGrid = grid?.[String(weeklyHours)] ?? grid?.[weeklyHours]
    if (Number.isFinite(fromGrid)) return fromGrid
    return (
        SUMMER_HOURS_PER_MONTH[weeklyHours] ??
        Math.round(weeklyHours * WEEKS_PER_MONTH)
    )
}

// Heures mensuelles. Cours partagés ET particuliers facturent sur la même
// cadence ~4,33 sem/mois (grille backend `hours_per_month`) — plus de ×4 pour
// les particuliers.
export const getSummerCoursesHoursPerMonth = (config, summerConfig = null) => {
    const hoursPerWeek = getSummerCoursesHoursPerWeek(config)
    if (!hoursPerWeek) return 0
    return coursesHoursPerMonth(hoursPerWeek, summerConfig)
}

// Descripteur du tarif Cours d'été mensuel. Cours partagés : prix résolu depuis
// la grille fast-track (API `staticData.summer_config.pricing_grid` prioritaire,
// grille constante du funnel en repli) → display == tarif facturé par la vente
// différée. Cours particuliers : `taux horaire backend × heures/mois` (cadence
// ~4,33 sem/mois), sans promo. `null` si config incomplète.
export const getSummerCoursesPriceDescriptor = (
    config,
    summerConfig = null
) => {
    if (!config) return null
    const hoursPerWeek = getSummerCoursesHoursPerWeek(config)
    const offerType = config.offerType
    if (!hoursPerWeek || !offerType) return null

    const hoursPerMonth = getSummerCoursesHoursPerMonth(config, summerConfig)

    let monthlyPrice
    let flexEquivalentPrice
    if (offerType === SUMMER_COURSES_OFFER_TYPE.SHARED) {
        const { price, strike } = resolveSummerPricing(
            hoursPerWeek,
            true,
            summerConfig?.pricing_grid
        )
        monthlyPrice = price
        flexEquivalentPrice = strike ?? price
    } else {
        // Cours particuliers (offre engagée) : taux horaire engagé backend du
        // palier d'heures/mois × heures/mois (≈ ×4,33), sans prix barré
        // (flexEquivalentPrice == monthlyPrice → savings 0).
        const perHour = getSummerCoursesPrivatePerHour(summerConfig, hoursPerMonth)
        if (!perHour || !hoursPerMonth) return null
        monthlyPrice = Math.round(perHour * hoursPerMonth)
        flexEquivalentPrice = monthlyPrice
    }

    const hourlyPrice =
        hoursPerMonth > 0
            ? Math.round((monthlyPrice / hoursPerMonth) * 10) / 10
            : 0
    const savings = Math.max(0, flexEquivalentPrice - monthlyPrice)
    return {
        monthlyPrice,
        flexEquivalentPrice,
        hourlyPrice,
        hoursPerMonth,
        hoursPerWeek,
        savings,
        isHeroOffer:
            offerType === SUMMER_COURSES_OFFER_TYPE.SHARED &&
            hoursPerWeek === 3,
    }
}

// Default = pack 3 créneaux partagés du matin (offre phare).
export const SUMMER_COURSES_DEFAULT_CONFIG = {
    offerType: SUMMER_COURSES_OFFER_TYPE.SHARED,
    slots_possible: [...SUMMER_COURSES_DEFAULT_SLOTS],
    hoursPerWeek: null,
    subjectIds: [],
}

export const getSummerCoursesOfferTypeLabel = (offerType) =>
    offerType === SUMMER_COURSES_OFFER_TYPE.PRIVATE
        ? "Cours particuliers"
        : "Cours partagés"

// Label compact des créneaux sélectionnés (utilisé dans la PlanCard).
// `slotsPossible` est au format canonical "Monday 10h-11h" ; on traduit
// le jour en français pour l'affichage.
export const getSummerCoursesTimeslotsLabel = (slotsPossible = []) =>
    slotsPossible
        .map((slot) => SUMMER_SHARED_TIMESLOTS.find((t) => t.slot === slot))
        .filter(Boolean)
        .map((t) => `${t.dayLabel} ${t.timeLabel}`)
        .join(" · ")

// Cutoff d'éligibilité au réengagement 12 mois à la rentrée : on ne
// propose l'offre qu'aux parents dont l'engagement actuel se termine
// avant le 31/07 de l'année en cours (sinon ils ont déjà un engagement
// qui couvre la rentrée). Les parents sans engagement courant sont
// systématiquement éligibles. Single source of truth via
// `getSummerConfigSafe(summerConfig).engagementCut`.

export const isResumeOfferEngagementEligible = (
    user,
    summerConfig,
    projectedCommitmentEndDate = null
) => {
    if (!user) return false
    // Projection prime — quand le cockpit fournit la date post-confirm
    // (cf. `projectCommitmentEndDate`), elle reflète l'engagement que
    // backend écrira au tap Confirmer. Sans, on retombe sur la valeur
    // backend brute pour les call sites legacy (portail, banner).
    const effectiveCommitEnd =
        projectedCommitmentEndDate ?? user.commitment_ends_at
    if (!effectiveCommitEnd) return true
    const { engagementCut } = getSummerConfigSafe(summerConfig)
    return moment(effectiveCommitEnd).isSameOrBefore(
        moment(engagementCut).endOf("day")
    )
}

// Nombre d'heures hebdo minimum pour ouvrir droit à la remise de
// réengagement (−50€ M1 + −50€ M2). Le bonus est réservé aux offres
// ≥ 2h/sem — les offres 1h/sem (et 1,5h/sem en particuliers) en sont
// explicitement exclues.
export const SUMMER_RENEWAL_DISCOUNT_MIN_WEEKLY_HOURS = 2

export const meetsRenewalDiscountFrequency = (frequency) =>
    typeof frequency === "number" &&
    frequency >= SUMMER_RENEWAL_DISCOUNT_MIN_WEEKLY_HOURS

// Canonical gate for the "−50€ M1 + −50€ M2 à la rentrée" discount. The
// bonus only makes sense for a GENUINE re-engagement at the rentrée: the
// parent's (projected) commitment must end BEFORE the rentrée resume, so
// that picking YEARLY_12M signs a fresh 12-month engagement. A parent whose
// commitment already covers the rentrée — e.g. an engagement merely
// stretched by the summer pauses past the window (12/07 → 12/09) — is
// mid-engagement, NOT re-engaging: no discount.
//
// That's why the cutoff is the RESUME date (≈ `!isCommitmentBeyondSummerProjected`)
// and NOT `engagementCut` (`engagement_tolerance_end`, 30/09 by default):
// the tolerance window exists for auto-pause eligibility and is too late —
// it overlaps the "engagement already covers the rentrée" zone.
//
// Single source of truth — every surface advertising the discount (Cockpit
// gift box, Rentrée plan card ribbon, Resume modal footer/card) and the
// backend confirmation mail (`SummerChoiceConfirmedNotification::isEngagementEligible`)
// must align with this gate. Callers MUST additionally check
// `meetsRenewalDiscountFrequency(frequency)` — the discount is reserved to
// offers ≥ 2h/sem.
export const shouldOfferRenewalDiscount = (
    user,
    summerConfig,
    projectedCommitmentEndDate = null
) => {
    if (!user) return false
    const effectiveCommitEnd =
        projectedCommitmentEndDate ?? user.commitment_ends_at
    // Pas d'engagement courant → réinscription effective à la rentrée → éligible.
    if (!effectiveCommitEnd) return true
    const { resumeDate } = getSummerConfigSafe(summerConfig)
    return moment(effectiveCommitEnd).isBefore(
        moment(resumeDate).startOf("day")
    )
}

// True when the parent has an active engagement that already extends
// past the end of the summer window (31/08). Such parents are already
// committed for the rentrée — the Cockpit must surface this engagement
// (read-only "engagement en cours jusqu'au X") rather than pretend the
// rentrée is "sans engagement". Used to freeze `with_commitment = true`
// on the Rentrée sub-form.
export const hasCommitmentBeyondSummer = (
    user,
    now = moment(),
    summerConfig
) => {
    const endsAt = user?.commitment_ends_at
        ? moment(user.commitment_ends_at)
        : null
    if (!endsAt || !endsAt.isValid()) return false
    if (endsAt.isSameOrBefore(now)) return false
    const { windowEnd } = getSummerConfigSafe(summerConfig)
    return endsAt.isAfter(moment(windowEnd).endOf("day"))
}

// "Source-of-truth" commit_ends_at projection that accounts for the
// parent's current draft pauses in the cockpit. We want the cockpit UI to
// show the engagement date THE PARENT WILL SEE AFTER CONFIRMING, not the
// one Stripe holds today (which is still pre-shift until they tap
// "Confirmer"). Aligns with `EagerSummerPrimitiveService::createPause()`
// which adds 31 days per pause-month to `commitment_ends_at`.
//
// `baseCommitmentEndDate` should be the pre-pause commit:
//  - if a summer auto-pause is already in flight backend-side, pass
//    `current_pause.previous_commitment_end_date` (the saved pre-shift)
//  - otherwise pass `user.commitment_ends_at` directly (no shift applied).
// Returns an ISO date string, or null when the base is missing/expired.
export const projectCommitmentEndDate = (
    baseCommitmentEndDate,
    julyChoice,
    augustChoice
) => {
    if (!baseCommitmentEndDate) return null
    const base = moment(baseCommitmentEndDate)
    if (!base.isValid()) return null
    const julyPause = julyChoice === SUMMER_MONTH_CHOICE.PAUSE
    const augustPause = augustChoice === SUMMER_MONTH_CHOICE.PAUSE
    const days = (julyPause ? 31 : 0) + (augustPause ? 31 : 0)
    return base.clone().add(days, "days").format()
}

// Same idea as `hasCommitmentBeyondSummer`, but operates on the projected
// commit so the cockpit reacts to LOCAL draft edits rather than waiting
// for the backend write. Used to drive the yearly-lock visualisation
// pre-confirm AND the Case 3 wipe transition.
export const isCommitmentBeyondSummerProjected = (
    projectedCommitmentEndDate,
    now = moment(),
    summerConfig
) => {
    if (!projectedCommitmentEndDate) return false
    const endsAt = moment(projectedCommitmentEndDate)
    if (!endsAt.isValid()) return false
    if (endsAt.isSameOrBefore(now)) return false
    const { windowEnd } = getSummerConfigSafe(summerConfig)
    return endsAt.isAfter(moment(windowEnd).endOf("day"))
}

// Fenêtre éligible pour la date de reprise effective de l'abonnement
// (`getSummerConfigSafe(summerConfig).resumeDate` → `.resumeEnd`). La
// période entre le 1er septembre et la date choisie est implicitement
// pausée (le parent décale sa rentrée sans perdre la rente saisonnière).

// Mois éditables individuellement au Summer Cockpit.
export const SUMMER_MONTHS = {
    JULY: "july",
    AUGUST: "august",
}

// Type d'offre à la reprise (1er → 7 sept).
export const SUMMER_RESUME_TYPE = {
    FLEX_SHARED: "flex_shared",
    FLEX_INDIVIDUAL: "flex_individual",
    AUTONOMY: "autonomy",
}

// Fréquences hebdo proposées par défaut, dérivées du catalogue Stripe
// (`Subscription::$pricesAmountsV2`). En pratique, le modal utilise plutôt
// `offerPricing` reconstruit depuis `pricingTableV2` pour rester aligné
// avec la grille de subscribe/create-subscription, mais on garde cette
// constante comme fallback si le pricing table n'est pas chargé.
export const SUMMER_RESUME_FREQUENCIES = {
    flex_shared: [1, 2, 3, 4, 5],
    flex_individual: [1, 1.5, 2, 3, 4, 5],
}

export const SUMMER_RESUME_COMMITMENT = {
    MONTHLY: "monthly",
    YEARLY_12M: "yearly_12m",
}

// Remise offerte sur les 2 premiers mois quand le parent souscrit
// à un engagement 12 mois à la reprise.
export const SUMMER_YEARLY_COMMITMENT_DISCOUNT = 100

export const getSummerResumeTypeLabel = (type) => {
    switch (type) {
        case SUMMER_RESUME_TYPE.FLEX_SHARED:
            return "Cours partagés"
        case SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL:
            return "Cours particuliers"
        case SUMMER_RESUME_TYPE.AUTONOMY:
            return "Mode Autonome"
        default:
            return "À choisir"
    }
}

export const getSummerResumeTypeDescription = (type) => {
    switch (type) {
        case SUMMER_RESUME_TYPE.FLEX_SHARED:
            return "Cours en petit groupe (2 à 4 élèves)"
        case SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL:
            return "Cours individuels avec votre tuteur"
        case SUMMER_RESUME_TYPE.AUTONOMY:
            return "Plateforme + IA pédagogique, sans tuteur attitré"
        default:
            return ""
    }
}

export const getSummerResumeTypeIcon = (type) => {
    switch (type) {
        case SUMMER_RESUME_TYPE.FLEX_SHARED:
            return "users"
        case SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL:
            return "user"
        case SUMMER_RESUME_TYPE.AUTONOMY:
            return "user-graduate"
        default:
            return "question-circle"
    }
}

// Label compact de l'offre rentrée — utilisé dans le récap et la
// state line du portail.
export const getSummerResumeOfferLabel = ({
    type,
    frequency,
    commitment,
} = {}) => {
    if (!type) return null
    const typeLabel = getSummerResumeTypeLabel(type)
    if (type === SUMMER_RESUME_TYPE.AUTONOMY) {
        return commitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M
            ? `${typeLabel} · engagement 12 mois`
            : typeLabel
    }
    const freq = frequency ? `${frequency}h / sem` : null
    const commit =
        commitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M
            ? "engagement 12 mois"
            : "sans engagement"
    return [typeLabel, freq, commit].filter(Boolean).join(" · ")
}

// Options accessibles pour chaque mois (juillet / août). Triées dans
// l'ordre d'apparition à l'écran.
export const SUMMER_MONTH_CHOICE = {
    SUMMER_COURSES: "summer_courses",
    AUTONOMY: "autonomy",
    PAUSE: "pause",
}

export const getSummerMonthLabel = (month) => {
    if (month === SUMMER_MONTHS.JULY) return "Juillet"
    if (month === SUMMER_MONTHS.AUGUST) return "Août"
    return ""
}

export const getSummerMonthDateRange = (month) => {
    if (month === SUMMER_MONTHS.JULY) return "Du 1er au 31 juillet"
    if (month === SUMMER_MONTHS.AUGUST) return "Du 1er au 31 août"
    return ""
}

export const getSummerMonthChoiceLabel = (choice) => {
    switch (choice) {
        case SUMMER_MONTH_CHOICE.SUMMER_COURSES:
            return "Cours d'été"
        case SUMMER_MONTH_CHOICE.AUTONOMY:
            return "Mode Autonome"
        case SUMMER_MONTH_CHOICE.PAUSE:
            return "Pause"
        default:
            return "À choisir"
    }
}

export const getSummerMonthChoiceIcon = (choice) => {
    switch (choice) {
        case SUMMER_MONTH_CHOICE.SUMMER_COURSES:
            return "sun"
        case SUMMER_MONTH_CHOICE.AUTONOMY:
            return "user-graduate"
        case SUMMER_MONTH_CHOICE.PAUSE:
            return "pause-circle"
        default:
            return "question-circle"
    }
}

export const getSummerMonthChoiceDescription = (choice) => {
    switch (choice) {
        case SUMMER_MONTH_CHOICE.SUMMER_COURSES:
            return `Programme personnalisé avec un tuteur — ${SUMMER_PRICES.SUMMER_COURSES_PRICE}€ pour juillet et août`
        case SUMMER_MONTH_CHOICE.AUTONOMY:
            return `Plateforme + IA + 1 cours/mois — ${SUMMER_PRICES.AUTONOMY_MONTHLY_PRICE}€/mois`
        case SUMMER_MONTH_CHOICE.PAUSE:
            return "Aucun prélèvement, aucune séance"
        default:
            return ""
    }
}

// Bullets marketing par option, affichés dans le cockpit pour aider
// le parent à comparer côté à côté. Cours d'été = hero (4 bullets),
// Autonomie = standard (3 bullets), Pause = light (2 bullets).
export const getSummerMonthChoiceBullets = (choice) => {
    switch (choice) {
        case SUMMER_MONTH_CHOICE.SUMMER_COURSES:
            return [
                "Programme personnalisé construit avec un tuteur expert",
                "Énoncés illimités générés par notre IA pédagogique",
                "Cours en ligne en petit groupe (3-4 élèves)",
                "Suivi mensuel des progrès envoyé aux parents",
            ]
        case SUMMER_MONTH_CHOICE.AUTONOMY:
            return [
                "Accès illimité à la plateforme + IA pédagogique",
                "Énoncés et corrections à la demande",
                "1 cours offert avec un tuteur dans le mois",
            ]
        case SUMMER_MONTH_CHOICE.PAUSE:
            return [
                "Aucun prélèvement durant la période",
                "Reprise automatique de votre abonnement à la rentrée",
            ]
        default:
            return []
    }
}

// Récupère le choice (string) à partir d'un slot mensuel — supporte
// l'ancienne shape (string direct) et la nouvelle ({ choice, coursesConfig }).
export const getSummerMonthChoiceValue = (slot) => {
    if (!slot) return null
    if (typeof slot === "string") return slot
    return slot.choice ?? null
}

export const getSummerMonthCoursesConfig = (slot) => {
    if (!slot || typeof slot === "string") return null
    return slot.coursesConfig ?? null
}

// Phrase mensuelle compacte pour la state card du portail. Si les
// deux mois sont identiques, on agrège. Sinon on les distingue.
// Accepte soit la string legacy soit la shape { choice, coursesConfig }.
export const getSummerMonthlyTransitionLabel = (julySlot, augustSlot) => {
    const july = getSummerMonthChoiceValue(julySlot)
    const august = getSummerMonthChoiceValue(augustSlot)
    if (!july && !august) return null
    const label = (choice) => {
        switch (choice) {
            case SUMMER_MONTH_CHOICE.SUMMER_COURSES:
                return "Cours d'été"
            case SUMMER_MONTH_CHOICE.AUTONOMY:
                return "Mode Autonome"
            case SUMMER_MONTH_CHOICE.PAUSE:
                return "Pause"
            default:
                return "À choisir"
        }
    }
    if (july && august && july === august) {
        if (july === SUMMER_MONTH_CHOICE.PAUSE) {
            return "Pause estivale du 1er juillet au 31 août — aucun prélèvement"
        }
        return `${label(july)} du 1er juillet au 31 août`
    }
    if (july && august) {
        return `Juillet : ${label(july)} · Août : ${label(august)}`
    }
    if (july) return `Juillet : ${label(july)} · Août : à choisir`
    return `Juillet : à choisir · Août : ${label(august)}`
}

// Recalcule le `status` legacy depuis les choix mensuels — priorité au
// plus engageant en cas de mix (Cours d'été > Autonomie > Pause). Le
// banner / status line lisent toujours `status` donc on garde la
// projection synchronisée. Accepte la shape legacy (string) ou la
// nouvelle ({ choice, coursesConfig }).
export const deriveSummerStatusFromChoices = (julySlot, augustSlot) => {
    const july = getSummerMonthChoiceValue(julySlot)
    const august = getSummerMonthChoiceValue(augustSlot)
    if (!july && !august) return SUMMER_CHOICE_STATUS.VIEWED_NO_CHOICE
    const picks = [july, august].filter(Boolean)
    if (picks.includes(SUMMER_MONTH_CHOICE.SUMMER_COURSES)) {
        return SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES
    }
    if (picks.includes(SUMMER_MONTH_CHOICE.AUTONOMY)) {
        return SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY
    }
    return SUMMER_CHOICE_STATUS.CHOSE_PAUSE
}

// Project the rentrée offer (type + frequency) onto the backend's
// `resume_preference` enum so the auto-pause / dispatch command can route
// the cohort even when the cockpit only carries the cockpit-level shape.
// FLEX_SHARED / FLEX_INDIVIDUAL + frequency → hours_2h / hours_3h / etc.
// AUTONOMY → autonomy. Anything else (no type, unknown frequency) →
// undecided so the backend can still mark the rentrée as confirmed without
// reading a stale "not_set".
export const deriveResumePreferenceFromConfig = ({ type, frequency } = {}) => {
    if (!type) return RESUME_PREFERENCE.NOT_SET
    if (type === SUMMER_RESUME_TYPE.AUTONOMY) return RESUME_PREFERENCE.AUTONOMY
    if (
        type === SUMMER_RESUME_TYPE.FLEX_SHARED ||
        type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL
    ) {
        switch (frequency) {
            case 1:
                return RESUME_PREFERENCE.HOURS_1H
            case 1.5:
                return RESUME_PREFERENCE.HOURS_1_5H
            case 2:
                return RESUME_PREFERENCE.HOURS_2H
            case 3:
                return RESUME_PREFERENCE.HOURS_3H
            case 4:
                return RESUME_PREFERENCE.HOURS_4H
            case 5:
                return RESUME_PREFERENCE.HOURS_5H
            case 6:
                return RESUME_PREFERENCE.HOURS_6H
            default:
                return RESUME_PREFERENCE.UNDECIDED
        }
    }
    return RESUME_PREFERENCE.UNDECIDED
}

// Type d'offre rentrée — projection du cockpit-level `resumeType` vers le
// champ explicite que le backend persiste désormais aux côtés de
// `resume_preference` (commit Bug 2 fix). 'shared' / 'individual' /
// 'autonomy'. Renvoie null si pas de type sélectionné — le backend
// validera comme nullable.
export const RESUME_OFFER_TYPE = {
    SHARED: "shared",
    INDIVIDUAL: "individual",
    AUTONOMY: "autonomy",
}

export const deriveResumeOfferTypeFromConfig = ({ type } = {}) => {
    if (!type) return null
    if (type === SUMMER_RESUME_TYPE.AUTONOMY) return RESUME_OFFER_TYPE.AUTONOMY
    if (type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL)
        return RESUME_OFFER_TYPE.INDIVIDUAL
    if (type === SUMMER_RESUME_TYPE.FLEX_SHARED) return RESUME_OFFER_TYPE.SHARED
    return null
}

// Reverse of `deriveResumePreferenceFromConfig` — used when hydrating the
// Summer slice from the UserResource payload (the backend only persists the
// 3-dimensions snapshot, not the granular rentrée config). We default the
// type to `FLEX_SHARED` (hero offer) when the preference is an hour band
// since the backend doesn't distinguish shared vs individual. The parent can
// still flip to Individual via the cockpit modal.
export const deriveResumeConfigFromPreference = (preference) => {
    if (!preference || preference === RESUME_PREFERENCE.NOT_SET) {
        return { type: null, frequency: null }
    }
    if (preference === RESUME_PREFERENCE.AUTONOMY) {
        return { type: SUMMER_RESUME_TYPE.AUTONOMY, frequency: null }
    }
    if (preference === RESUME_PREFERENCE.UNDECIDED) {
        return { type: null, frequency: null }
    }
    const frequencyByPreference = {
        [RESUME_PREFERENCE.HOURS_1H]: 1,
        [RESUME_PREFERENCE.HOURS_1_5H]: 1.5,
        [RESUME_PREFERENCE.HOURS_2H]: 2,
        [RESUME_PREFERENCE.HOURS_3H]: 3,
        [RESUME_PREFERENCE.HOURS_4H]: 4,
        [RESUME_PREFERENCE.HOURS_5H]: 5,
        [RESUME_PREFERENCE.HOURS_6H]: 6,
    }
    const frequency = frequencyByPreference[preference] ?? null
    if (!frequency) return { type: null, frequency: null }
    // 1.5h/week only exists in the Particuliers catalogue — a FLEX_SHARED
    // default would rebuild an unselectable combination and the modal's
    // frequency-reset effect would silently drop the hours. Every other band
    // exists in both catalogues, so the shared default is safe (and the
    // `resume_offer_type` override refines it when present).
    if (preference === RESUME_PREFERENCE.HOURS_1_5H) {
        return { type: SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL, frequency }
    }
    return { type: SUMMER_RESUME_TYPE.FLEX_SHARED, frequency }
}

// Reverse of `deriveSummerStatusFromChoices` — when the backend reports a
// confirmed cohort signal (`summer_summer_status`) but the granular per-month
// choices were never posted (e.g. admin override via the BO), we project a
// coarse per-month slot from the status so the cockpit can render the picked
// trajectory. Both months take the same choice; the parent can re-edit each
// month individually if needed. Returns `null` for non-confirmed statuses.
export const deriveMonthSlotsFromStatus = (status) => {
    switch (status) {
        case SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES:
            return {
                choice: SUMMER_MONTH_CHOICE.SUMMER_COURSES,
                coursesConfig: null,
            }
        case SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY:
            return { choice: SUMMER_MONTH_CHOICE.AUTONOMY, coursesConfig: null }
        case SUMMER_CHOICE_STATUS.CHOSE_PAUSE:
        case SUMMER_CHOICE_STATUS.AUTO_PAUSED:
            return { choice: SUMMER_MONTH_CHOICE.PAUSE, coursesConfig: null }
        default:
            return null
    }
}

// Whether the backend has a confirmed cohort signal worth hydrating into
// the Summer slice. Used by the reducer to decide if `getUser._SUCCESS` (or
// equivalent) should seed the local state. `not_visited` and missing values
// are no-ops (the local slice is the source of truth pre-confirmation).
export const isConfirmedSummerStatus = (status) =>
    [
        SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES,
        SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY,
        SUMMER_CHOICE_STATUS.CHOSE_PAUSE,
        SUMMER_CHOICE_STATUS.AUTO_PAUSED,
    ].includes(status)

// Returns the operational summer year exposed by the backend via
// `staticData.summer_config.current_year`. Falls back to the local
// calendar year when the staticData slice is missing (e.g. before the
// /static-data response lands), so wording never breaks.
//
// Components consume this helper via a `mapStateToProps` getter pulling
// `state.data.staticData?.summer_config?.current_year` rather than
// hardcoding "2026" in their JSX. When the backend bumps the year (cron
// rollover or admin override), all UI strings track automatically.
export const getSummerYearFromStaticData = (staticData) => {
    const fromBackend = staticData?.summer_config?.current_year
    if (typeof fromBackend === "number" && Number.isInteger(fromBackend)) {
        return fromBackend
    }
    return moment().year()
}

/* global process */
const FLAG_ENV =
    typeof process !== "undefined" && process && process.env
        ? process.env.EXPO_PUBLIC_SUMMER_2026_ENABLED
        : undefined

export const isSummer2026FlagForced = () =>
    FLAG_ENV === "true" || FLAG_ENV === "1"

export const isSummer2026Window = (now = moment(), summerConfig) => {
    const { cockpitOpen, cockpitClose } = getSummerConfigSafe(summerConfig)
    return now.isBetween(
        moment(cockpitOpen),
        moment(cockpitClose).endOf("day"),
        undefined,
        "[]"
    )
}

// Gate UNIQUE de la surface parent du cockpit d'été : bandeau, section
// « Été », entrées « Modifier mes choix d'été », écran `summer-cockpit`.
//
// L'interrupteur saisonnier passe AVANT la fenêtre de dates et avant le forçage
// local : une extinction doit s'imposer à tout le monde, y compris à un poste
// de dev qui aurait gardé `EXPO_PUBLIC_SUMMER_2026_ENABLED=true`. Un
// interrupteur qu'un env client contourne n'en est pas un.
export const isSummer2026Visible = (now = moment(), summerConfig) => {
    const { cockpitEnabled } = getSummerConfigSafe(summerConfig)
    if (!cockpitEnabled) return false
    return isSummer2026FlagForced() || isSummer2026Window(now, summerConfig)
}

// Bandeau marketing été (SummerBannerCard) — condition d'affichage unique,
// partagée entre le portail "Mon abonnement" et la home parent : fenêtre
// cockpit ouverte, cohorte éligible (manual sub / generic trial / abonnement
// churné exclus — le cockpit suppose une trajectoire Stripe self-service à
// modifier), et choix d'été PAS encore entériné backend. Un brouillon Redux
// non soumis ne masque jamais le bandeau : le parent doit garder l'entry
// point pour finaliser. Les élèves déjà en Mode Autonome SONT éligibles : ils
// organisent leur été comme les autres (le picker de reprise rentrée force le
// re-choix d'une offre courante, l'option autonomie y ayant été retirée).
// `user` doit porter `summer_summer_status` (UserResource) et
// `subscription_state` (UserSubscriptionResource).
export const isSummerBannerVisible = (user, summerConfig) => {
    const isManualSub = !!user?.subscription_state?.is_manual_sub
    const isOnGenericTrial =
        user?.subscription_state?.status === "on_generic_trial"
    // Churned / non-subscribed (ended, expired trial, no recurring sub) →
    // the cockpit operates on an active subscription, so the banner (which
    // navigates into the cockpit) must not be offered. Mirrors the
    // `summerEligible` gate in ParentSubscriptionPortalScreen and the
    // backend submit guard.
    const isChurnedSubscription = ["ended", "expired_trial", "-"].includes(
        user?.subscription_state?.status
    )
    // "New user" venu par le fast track Cours d'été : non routé par le cockpit
    // REG→SUM, donc pas de bandeau marketing été. Mirror du gate backend
    // (UserResource.has_summer_fast_track) et de ParentSubscriptionPortalScreen.
    const hasSummerFastTrack = !!user?.has_summer_fast_track
    return (
        isSummer2026Visible(undefined, summerConfig) &&
        !isManualSub &&
        !isOnGenericTrial &&
        !isChurnedSubscription &&
        !hasSummerFastTrack &&
        !isConfirmedSummerStatus(user?.summer_summer_status)
    )
}

// ---------------------------------------------------------------------------
// Phase + conditional UI helpers (cockpit visibility, month-card editability,
// "Changer matières/horaires" transition lock).
//
// Single source of truth : `getSummerConfigSafe(summerConfig)` lit
// `staticData.summer_config` (backend) avec un fallback sur l'année
// calendaire courante :
//  - cockpit.open      → `.cockpitOpen`   (Y-05-15)
//  - window.start      → `.windowStart`   (Y-07-01)
//  - window.end        → `.windowEnd`     (Y-08-31)
//  - grace post-window → `.graceDays`     (7 par défaut)
// ---------------------------------------------------------------------------

export const SUMMER_PHASE = {
    PRE_WINDOW: "pre_window",
    COCKPIT_OPEN_PRE_JULY: "cockpit_open_pre_july",
    JULY_ACTIVE: "july_active",
    AUGUST_ACTIVE: "august_active",
    POST_WINDOW_GRACE: "post_window_grace",
    POST_WINDOW_DONE: "post_window_done",
}

// Renvoie la phase courante par rapport à la fenêtre été 2026.
// - PRE_WINDOW            : avant cockpit.open (banner caché)
// - COCKPIT_OPEN_PRE_JULY : cockpit ouvert, fenêtre pas encore commencée
// - JULY_ACTIVE           : juillet en cours (1/07 → 31/07)
// - AUGUST_ACTIVE         : août en cours (1/08 → 31/08)
// - POST_WINDOW_GRACE     : fin de fenêtre + N jours (carte état "Reprise")
// - POST_WINDOW_DONE      : au-delà de la grace (tout disparaît)
export const getSummerPhase = (now = moment(), summerConfig) => {
    const cfg = getSummerConfigSafe(summerConfig)
    const cockpitOpen = moment(cfg.cockpitOpen).startOf("day")
    const julyStart = moment(cfg.windowStart).startOf("day")
    const augustStart = moment(cfg.windowStart).startOf("day").add(1, "month")
    const windowEnd = moment(cfg.windowEnd).endOf("day")
    const graceEnd = moment(cfg.windowEnd)
        .endOf("day")
        .add(cfg.graceDays, "days")

    if (now.isBefore(cockpitOpen)) return SUMMER_PHASE.PRE_WINDOW
    if (now.isBefore(julyStart)) return SUMMER_PHASE.COCKPIT_OPEN_PRE_JULY
    if (now.isBefore(augustStart)) return SUMMER_PHASE.JULY_ACTIVE
    if (now.isSameOrBefore(windowEnd)) return SUMMER_PHASE.AUGUST_ACTIVE
    if (now.isSameOrBefore(graceEnd)) return SUMMER_PHASE.POST_WINDOW_GRACE
    return SUMMER_PHASE.POST_WINDOW_DONE
}

// True if a parent can still edit the given month card at `now`.
// Règle : un mois reste éditable tant qu'il n'est pas entamé.
//   - juillet : éditable jusqu'au 30/06 23:59
//   - août    : éditable jusqu'au 31/07 23:59
export const isMonthCardEditable = (month, now = moment(), summerConfig) => {
    const { windowStart } = getSummerConfigSafe(summerConfig)
    if (month === SUMMER_MONTHS.JULY) {
        return now.isBefore(moment(windowStart).startOf("day"))
    }
    if (month === SUMMER_MONTHS.AUGUST) {
        return now.isBefore(moment(windowStart).startOf("day").add(1, "month"))
    }
    return false
}

// True dès que le parent a posé un choix d'été qui mérite d'être verrouillé
// (i.e. ses matières/horaires d'été ont été figées au moment du choix).
// Lit `summer_summer_status` de la User Resource — single source of truth
// côté backend pour les cohorts confirmées.
//
// Cohort C2 (pause manuelle déjà programmée qui couvre toute la fenêtre)
// : le parent ne peut pas poser de choix mensuel (les mois sont déjà
// neutralisés par la pause Stripe). Pour eux, la finalisation est portée
// par `summer_resume_preference` seul — dès qu'un resume_preference
// explicite est posé (autre que not_set), la trajectoire d'été est
// considérée comme finalisée même si `summer_summer_status` reste à
// `viewed_no_choice`.
export const hasFinalizedSummerChoice = (user, summerConfig) => {
    const status = user?.summer_summer_status
    if (
        status &&
        status !== SUMMER_CHOICE_STATUS.NOT_VISITED &&
        status !== SUMMER_CHOICE_STATUS.VIEWED_NO_CHOICE
    ) {
        return true
    }
    // C2 fallback : pause manuelle couvre tout + resume_preference posé.
    const coverage = getEffectiveSummerPauseCoverage(user, summerConfig)
    const resumePreference = user?.summer_resume_preference
    if (
        coverage.julyCovered &&
        coverage.augustCovered &&
        resumePreference &&
        resumePreference !== RESUME_PREFERENCE.NOT_SET
    ) {
        return true
    }
    return false
}

// Cohort C2 (pause overlap mid-window) — si une pause active couvre une
// partie de la fenêtre été, seuls les mois NON couverts par cette pause
// restent éligibles au choix Summer. La couverture est calculée via
// `getEffectiveSummerPauseCoverage` (même logique d'overlap que celle
// affichée dans le cockpit en read-only), pour rester cohérent avec ce
// que le parent voit à l'écran.
//
// Cas typique post-cockpit : un parent confirme `juillet=pause` (seul) →
// l'EagerSummerPrimitiveService pose une pause Stripe juillet (windowStart →
// augustStart). `planned_start_date = 1/8 00:00`, donc la pause couvre
// strictement juillet — août doit rester éligible (le parent va y poser
// cours_ete/autonomy/pause indépendamment). Un cutoff strict sur la
// resume date ratait ce cas (1/8 > 31/7 endOfDay) et neutralisait à tort
// la card août dans le cockpit en mode "Modifier".
export const getEligibleSummerMonths = (user, summerConfig) => {
    const allMonths = [SUMMER_MONTHS.JULY, SUMMER_MONTHS.AUGUST]
    const coverage = getEffectiveSummerPauseCoverage(user, summerConfig)
    if (!coverage.julyCovered && !coverage.augustCovered) return allMonths
    const eligible = []
    if (!coverage.julyCovered) eligible.push(SUMMER_MONTHS.JULY)
    if (!coverage.augustCovered) eligible.push(SUMMER_MONTHS.AUGUST)
    return eligible
}

// Couverture de la fenêtre été par une pause manuelle déjà programmée.
// Sert au cockpit pour matérialiser un mois en read-only "Pause déjà
// programmée du X au Y" plutôt que de proposer un choix mensuel qui
// serait écrasé par la pause Stripe en place.
//
// Lit `subscription_state` (paused / on_grace_period_before_pause) :
//  - `end_date`            = début de la pause (date de fin de l'abo courant)
//  - `planned_start_date`  = date de reprise post-pause
// Une pause couvre un mois dès qu'elle chevauche la période juillet
// (1/07 → 31/07) ou août (1/08 → 31/08). Si pas de pause manuelle, les
// deux flags retournent false.
export const getEffectiveSummerPauseCoverage = (user, summerConfig) => {
    const empty = {
        julyCovered: false,
        augustCovered: false,
        manualPauseStartDate: null,
        manualPauseEndDate: null,
    }
    const subState = user?.subscription_state
    const isPaused =
        subState?.status === "paused" ||
        subState?.status === "on_grace_period_before_pause"
    if (!isPaused) return empty
    const pauseStart = subState?.end_date ? moment(subState.end_date) : null
    const pauseEnd = subState?.planned_start_date
        ? moment(subState.planned_start_date)
        : null
    if (!pauseStart?.isValid() || !pauseEnd?.isValid()) return empty
    const { windowStart, windowEnd } = getSummerConfigSafe(summerConfig)
    const julyStart = moment(windowStart).startOf("day")
    const julyEnd = moment(windowStart).endOf("month")
    const augustStart = moment(windowStart).startOf("day").add(1, "month")
    const augustEnd = moment(windowEnd).endOf("day")
    // Une pause couvre un mois dès qu'elle commence avant la fin du mois
    // ET se termine après le début du mois (intervalles ouverts à droite
    // sur la reprise = parent reprend le 1er du mois → mois pas couvert).
    const overlaps = (rangeStart, rangeEnd) =>
        pauseStart.isSameOrBefore(rangeEnd) && pauseEnd.isAfter(rangeStart)
    // Si le parent a posé un choix via le cockpit pour ce mois, la pause
    // détectée est probablement la primitive eager qu'on vient nous-mêmes
    // de poser — pas une pause externe. On laisse le mois éditable pour
    // que le parent puisse changer d'avis. Si le parent n'a rien posé
    // (not_visited / viewed_no_choice), on garde le lock pour respecter
    // une éventuelle pause manuelle pré-existante.
    const ACTIVE_CHOICES = new Set([
        SUMMER_CHOICE_STATUS.CHOSE_PAUSE,
        SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES,
        SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY,
        SUMMER_CHOICE_STATUS.AUTO_PAUSED,
    ])
    const julyChoiceSet = ACTIVE_CHOICES.has(user?.summer_july_status)
    const augustChoiceSet = ACTIVE_CHOICES.has(user?.summer_august_status)
    return {
        julyCovered: !julyChoiceSet && overlaps(julyStart, julyEnd),
        augustCovered: !augustChoiceSet && overlaps(augustStart, augustEnd),
        manualPauseStartDate: subState?.end_date ?? null,
        manualPauseEndDate: subState?.planned_start_date ?? null,
    }
}

export const getStudentFirstName = (user) =>
    user?.students?.[0]?.firstname || user?.firstname || null

export const getSummerChoiceLabel = (status) => {
    switch (status) {
        case SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES:
            return "Cours d'été"
        case SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY:
            return "Mode Autonome"
        case SUMMER_CHOICE_STATUS.CHOSE_PAUSE:
            return "Pause confirmée"
        case SUMMER_CHOICE_STATUS.AUTO_PAUSED:
            return "Pause auto"
        default:
            return "À configurer"
    }
}

export const hasSummerChoice = (status) =>
    [
        SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES,
        SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY,
        SUMMER_CHOICE_STATUS.CHOSE_PAUSE,
        SUMMER_CHOICE_STATUS.AUTO_PAUSED,
    ].includes(status)

// Truthy when the parent has at least started editing the summer
// (any month has a choice set) OR the legacy status flag is posted.
// Used by the portal to switch the entry button label and hide the
// marketing banner post-choice.
export const hasSummerChoiceSet = (summerState) => {
    if (!summerState) return false
    if (hasSummerChoice(summerState.status)) return true
    return !!(
        getSummerMonthChoiceValue(summerState.july) ||
        getSummerMonthChoiceValue(summerState.august)
    )
}

export const isBeforeSummerPeriod = (now = moment(), summerConfig) => {
    const { windowStart } = getSummerConfigSafe(summerConfig)
    return now.isBefore(moment(windowStart))
}

export const getJuneTransitionLabel = (value) => {
    switch (value) {
        case JUNE_TRANSITION.CONTINUE_CURRENT:
            return "Continuer l'abonnement actuel"
        case JUNE_TRANSITION.PAUSE_EARLY:
            return "Pauser dès maintenant"
        case JUNE_TRANSITION.AUTONOMY_NOW:
            return "Passer en Autonomie immédiat"
        case JUNE_TRANSITION.BOOST_EXAMS:
            return "Intensifier pour les exams"
        default:
            return null
    }
}

// ---------------------------------------------------------------------------
// June cockpit block (read-only continuation by default + optional advanced
// pause). All June dates are derived from the same `getSummerConfigSafe()`
// window so the cockpit, the manual pause modal, and the portal share one
// source of truth (no duplicated June constants).
// ---------------------------------------------------------------------------

// Compact French label of the parent's CURRENT subscription formula
// (current_offer slug + hours/week), used by the June cockpit block's
// "poursuite de [formule] jusqu'au 30 juin" copy. Falls back to "votre
// formule actuelle" when the offer is unknown so the sentence stays natural.
const CURRENT_OFFER_LABELS_FR = {
    integral: "Cours particuliers",
    essential: "Cours partagés",
    autonomy: "Mode Autonome",
}

const HOURS_PER_MONTH_TO_PER_WEEK_FR = {
    4: 1,
    6: 1.5,
    9: 2,
    13: 3,
    17: 4,
    22: 5,
}

export const getCurrentFormulaLabel = (user) => {
    const offer = user?.current_offer
    const label = CURRENT_OFFER_LABELS_FR[offer]
    if (!label) return "votre formule actuelle"
    if (offer === "autonomy") return label
    const hoursPerMonth = user?.subscription_state?.hours_per_month
    const perWeek = hoursPerMonth
        ? HOURS_PER_MONTH_TO_PER_WEEK_FR[hoursPerMonth]
        : null
    return perWeek ? `${label} · ${perWeek}h/sem` : label
}

// June bounds for the early-pause date picker: 1 June → the day before the
// summer window opens (30 June by default). Derived from `windowStart`.
export const getSummerJuneBounds = (summerConfig) => {
    const { windowStart } = getSummerConfigSafe(summerConfig)
    const ws = moment(windowStart).startOf("day")
    return {
        // First selectable day of June.
        juneStart: ws.clone().subtract(1, "month"),
        // Last selectable day before the summer window opens (= 30 June).
        juneEnd: ws.clone().subtract(1, "day"),
    }
}

// Cockpit-managed window for the "Fin d'engagement" hiding rule on the portal.
// June is now folded into the cockpit, so the managed window runs from 1 June
// (windowStart − 1 month) through the summer window end (31 August). A
// commitment whose end date falls inside this range is surfaced via the
// cockpit, so the raw "Fin d'engagement : 30 juin" line is hidden to avoid the
// cross-screen incoherence.
export const isCommitmentInSummerCockpitWindow = (
    commitmentEndsAt,
    summerConfig
) => {
    if (!commitmentEndsAt) return false
    const end = moment(commitmentEndsAt)
    if (!end.isValid()) return false
    const { windowEnd } = getSummerConfigSafe(summerConfig)
    const { juneStart } = getSummerJuneBounds(summerConfig)
    return end.isBetween(
        juneStart.clone().startOf("day"),
        moment(windowEnd).endOf("day"),
        undefined,
        "[]"
    )
}

export const getResumePreferenceLabel = (value) => {
    switch (value) {
        case RESUME_PREFERENCE.HOURS_1H:
            return "Reprise 1h/sem"
        case RESUME_PREFERENCE.HOURS_1_5H:
            return "Reprise 1.5h/sem"
        case RESUME_PREFERENCE.HOURS_2H:
            return "Reprise 2h/sem"
        case RESUME_PREFERENCE.HOURS_3H:
            return "Reprise 3h/sem"
        case RESUME_PREFERENCE.HOURS_4H:
            return "Reprise 4h/sem"
        case RESUME_PREFERENCE.HOURS_5H:
            return "Reprise 5h/sem"
        case RESUME_PREFERENCE.HOURS_6H:
            return "Reprise 6h/sem"
        case RESUME_PREFERENCE.AUTONOMY:
            return "Reprise en Autonomie"
        case RESUME_PREFERENCE.UNDECIDED:
            return "À décider plus tard"
        default:
            return null
    }
}

// Hours/week derived from a resume_preference hour band. Returns null for
// non-hour preferences (autonomy, undecided, not_set).
export const getResumePreferenceHoursPerWeek = (value) => {
    switch (value) {
        case RESUME_PREFERENCE.HOURS_1H:
            return 1
        case RESUME_PREFERENCE.HOURS_1_5H:
            return 1.5
        case RESUME_PREFERENCE.HOURS_2H:
            return 2
        case RESUME_PREFERENCE.HOURS_3H:
            return 3
        case RESUME_PREFERENCE.HOURS_4H:
            return 4
        case RESUME_PREFERENCE.HOURS_5H:
            return 5
        case RESUME_PREFERENCE.HOURS_6H:
            return 6
        default:
            return null
    }
}

// Compact "plan + hours" label for the portal Rentrée block, derived from
// the backend `summer_resume_preference` and the current product offer
// (essential = Cours partagés, integral = Cours particuliers, autonomy =
// Mode Autonome). Returns just the plan label when no hours apply.
//
// Examples:
//   hours_3h + essential → "Cours partagés · 3h/sem"
//   hours_2h + integral  → "Cours particuliers · 2h/sem"
//   autonomy            → "Mode Autonome"
//   undecided / null    → "À configurer"
export const getRentreePlanLabel = ({
    resumePreference,
    resumeOfferType,
    currentOffer,
}) => {
    if (
        resumePreference === RESUME_PREFERENCE.AUTONOMY ||
        resumeOfferType === RESUME_OFFER_TYPE.AUTONOMY
    ) {
        return "Mode Autonome"
    }
    const hoursPerWeek = getResumePreferenceHoursPerWeek(resumePreference)
    if (!hoursPerWeek) {
        // Undecided / not_set / unknown — fall back to "À configurer".
        return "À configurer"
    }
    // Type d'offre rentrée — `resume_offer_type` posé explicitement par le
    // cockpit lève l'ambiguïté (un hours_2h peut être partagés OU
    // particuliers). Fallback sur l'offre courante pour rétrocompat sur les
    // rows posées avant que le champ existe.
    const planLabel =
        resumeOfferType === RESUME_OFFER_TYPE.INDIVIDUAL ||
        (!resumeOfferType && currentOffer === "integral")
            ? "Cours particuliers"
            : "Cours partagés"
    return `${planLabel} · ${hoursPerWeek}h/sem`
}

// Label for a month sub-block of the portal Summer section, derived from
// the backend per-month status snapshot (`summer_july_status` /
// `summer_august_status`). Both fields are exposed by `UserResource` and
// fall back to the legacy single-axis `summer_status` when the granular
// columns haven't been seeded yet, so the same mapping works for July and
// August.
export const getSummerMonthLabelFromStatus = (status) => {
    switch (status) {
        case SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES:
            return "Cours d'été"
        case SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY:
            return "Mode Autonome"
        case SUMMER_CHOICE_STATUS.CHOSE_PAUSE:
        case SUMMER_CHOICE_STATUS.AUTO_PAUSED:
            return "Pause"
        default:
            return null
    }
}

// Compact date line for a month sub-block of the portal Summer section.
// Mirrors the wording of the cockpit cards: meaningful when the parent
// chose Cours d'été or Mode Autonome, neutral "Aucun prélèvement" for
// Pause / Auto-pause. Used by both JUILLET and AOÛT blocks now that the
// per-month split is wired end-to-end.
export const getSummerMonthDateLineFromStatus = (month, status) => {
    const range =
        month === SUMMER_MONTHS.AUGUST
            ? "Du 1er au 31 août"
            : "Du 1er au 31 juillet"
    switch (status) {
        case SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES:
        case SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY:
            // Le label du mois reprend déjà l'offre — pas besoin de la
            // dupliquer ici. On garde juste la fenêtre de dates.
            return range
        case SUMMER_CHOICE_STATUS.CHOSE_PAUSE:
        case SUMMER_CHOICE_STATUS.AUTO_PAUSED:
            // "Aucun prélèvement" reste informatif (clarifie l'effet
            // billing) — pas redondant avec le label "Pause".
            return `${range} · Aucun prélèvement`
        default:
            return range
    }
}

// Date line for the JUIN sub-block of the portal Summer section — only
// meaningful when the parent advanced a June pause. The June slice runs from
// the picked start day through the end of June; the pause itself continues
// into July/August, described by those rows. Returns null otherwise. The end
// label is derived from the same window config as the picker (no duplicated
// "30 juin" constant).
export const getSummerJuneDateLine = (junePauseStartDate, summerConfig) => {
    if (!junePauseStartDate) return null
    const start = moment(junePauseStartDate)
    if (!start.isValid()) return null
    const startLabel = start.date() === 1 ? "1er" : `${start.date()}`
    const endLabel = getSummerJuneBounds(summerConfig).juneEnd.format("D MMMM")
    return `Du ${startLabel} au ${endLabel} · Aucun prélèvement`
}

// Date line for the JUIN sub-block when no June pause is advanced (default
// continuation): "[current formula] jusqu'au 30 juin". Mirrors the cockpit
// June block copy so the portal recap reads the same. End label derived from
// the same window config (no duplicated "30 juin" constant).
export const getSummerJuneContinuationDateLine = (user, summerConfig) => {
    const endLabel = getSummerJuneBounds(summerConfig).juneEnd.format("D MMMM")
    return `${getCurrentFormulaLabel(user)} jusqu'au ${endLabel}`
}

// Jour de reprise EFFECTIF de la famille, à annoncer partout où une reprise
// apparaît (récap été ligne RENTRÉE, phrase de grâce post-fenêtre, carte
// « Reprise planifiée »).
//
// Ce n'est PAS toujours le 1ᵉʳ septembre : le parent peut avoir décalé sa
// rentrée depuis le cockpit, et une pause vivante porte alors la vraie date.
// Le portail annonçait « Reprise le 1er septembre » à une famille dont la
// pause reprenait le 15 — la page contredisait le calendrier réel.
//
// Ordre de vérité, le plus engagé d'abord : le jalon de reprise POSÉ, puis la
// date résolue par le back (`upcoming_activation.resume_date`, qui intègre déjà
// pause + choix d'été), puis la reprise de la pause courante, puis le choix
// d'été brut (`user.summer_resume_date`), puis la date globale de config.
//
// Le JALON prime sur tout le reste — y compris quand il reprend plus TÔT :
// c'est lui que le cron applique. `resume_date` n'est que la date où un
// PROCHAIN jalon se poserait (le POST d'activation la prend comme défaut, puis
// la remplace par la date choisie par le parent sans jamais réécrire la pause
// ni le choix d'été) : une fois le jalon posé, elle décrit une trajectoire que
// plus personne n'exécute. Le bandeau « trou de couverture » la lisait encore
// et annonçait « aucun live du 29 au 31 août » à une famille qui ne reprenait
// que le 1ᵉʳ octobre — un trou d'un mois sous-annoncé à trois jours.
//
// @param plannedResume `upcoming_activation.planned.effective_at` (jalon posé)
// @param resolved     `upcoming_activation.resume_date` (déjà arbitré côté back)
// @param pauseResume  `pauseBalance.current_pause.resume_date`
// @param summerChoice `user.summer_resume_date`
// @param summerConfig payload `staticData.summer_config`
export const resolveDisplayResumeDate = ({
    plannedResume = null,
    resolved = null,
    pauseResume = null,
    summerChoice = null,
    summerConfig = null,
} = {}) => {
    const { resumeDate: globalResume } = getSummerConfigSafe(summerConfig)
    const candidate = [
        plannedResume,
        resolved,
        pauseResume,
        summerChoice,
        globalResume,
    ].find((value) => value && moment(value).isValid())

    return candidate ? moment(candidate).startOf("day") : null
}

// « le 1ᵉʳ septembre 2026 » / « le 15 septembre 2026 » — la date de reprise,
// précédée de son article.
//
// Simple habillage de `formatActivationDate`, qui porte déjà l'ordinal français,
// la locale posée sur l'instance et le millésime. Les deux libellés se croisent
// sur l'écran d'abonnement — « Reprise normale le 1ᵉʳ septembre 2026 » d'un côté,
// « Reprise le 1ᵉʳ septembre 2026 » de l'autre : deux implémentations de la même
// règle y donnaient deux orthographes du même jour.
export const getResumeDateLabel = (dateLike) => {
    const label = formatActivationDate(dateLike)

    return label ? `le ${label}` : null
}

// ---------------------------------------------------------------------------
// Décalage de l'engagement lié à une pause d'été cockpit.
//
// Round 1 du Summer 2026 MVP a introduit l'eager confirm : dès que le parent
// valide le cockpit, `EagerSummerPrimitiveService` pose la première primitive
// (pause / swap) et le backend renvoie un `commitment_ends_at` **déjà étiré**
// dans la `UserResource`. Le helper qui anticipait ce shift côté front
// (commit `99b9f3c86`) est donc redondant pour le flux nominal — l'engagement
// post-confirm est lu directement depuis `user.commitment_ends_at`.
//
// On garde la signature comme alias résilient : `oldCommitment` et
// `newCommitment` retournent tous les deux `user.commitment_ends_at`,
// `days = 0`. Les call sites (`SummerMonthPlanCard`, `SummerMonthEditModal`)
// continuent de gate l'affichage sur `shift.days > 0` et masquent donc
// naturellement la ligne "Engagement décalé de Nj" qui ferait double emploi
// avec la "Fin d'engagement" du portail. Conservé sous forme d'alias pour
// éviter une refacto cross-fichiers et pour absorber d'éventuelles races
// confirm-but-not-yet-posed.
//
// `pauseMonths` reste utilisé par les hooks legacy / tests pour vérifier
// que les durées canoniques (31 / 62 jours) restent constantes côté front.

// Reads the operational year from `summerConfig` and returns the calendar
// days in the target month. Returns 0 if the month is unknown.
export const getSummerMonthPauseDays = (month, summerConfig) => {
    const { year } = getSummerConfigSafe(summerConfig)
    if (month === SUMMER_MONTHS.JULY) {
        return moment(`${year}-07-01`).daysInMonth()
    }
    if (month === SUMMER_MONTHS.AUGUST) {
        return moment(`${year}-08-01`).daysInMonth()
    }
    return 0
}

export const getSummerPauseDaysForMonths = (pauseMonths = [], summerConfig) => {
    if (!Array.isArray(pauseMonths) || pauseMonths.length === 0) return 0
    return pauseMonths.reduce(
        (acc, month) => acc + getSummerMonthPauseDays(month, summerConfig),
        0
    )
}

// Alias mince sur `user.commitment_ends_at`. Renvoie toujours `days = 0` :
// le shift est désormais matérialisé côté backend au confirm (Round 1), donc
// il n'y a plus de calcul à faire côté front. `hasCommitment` reflète
// uniquement la présence d'un engagement futur sur la User Resource.
//
// La signature legacy (`pauseMonths`) est conservée pour rétrocompat des
// call sites — la valeur est ignorée côté logique mais validée par les
// tests via `getSummerPauseDaysForMonths`.
export const computeSummerPauseCommitmentShift = (
    user,
    pauseMonths = [],
    now = moment()
) => {
    const commitmentRaw = user?.commitment_ends_at
        ? moment(user.commitment_ends_at)
        : null
    const hasCommitment =
        !!commitmentRaw && commitmentRaw.isValid() && commitmentRaw.isAfter(now)
    const snapshot = hasCommitment ? commitmentRaw.clone() : null
    return {
        days: 0,
        hasCommitment,
        oldCommitment: snapshot,
        newCommitment: snapshot ? snapshot.clone() : null,
        // `pauseMonths` est passé en argument mais n'influence plus le
        // calcul — exposé en debug pour tracer les call sites encore en
        // place le temps de la migration vers une lecture directe de
        // `user.commitment_ends_at`.
        _legacyPauseMonths: Array.isArray(pauseMonths) ? pauseMonths : [],
    }
}

// Convenience helper aimed at the cockpit per-month cards. Conservé pour
// la même raison que `computeSummerPauseCommitmentShift` : alias résilient
// sur `user.commitment_ends_at`, zéro logique de calcul post Round 1.
export const computeSummerPauseCommitmentShiftForMonth = (
    user,
    month,
    now = moment()
) => computeSummerPauseCommitmentShift(user, [month], now)
